import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Site, RentmanAuthentication as RentmanAuthType } from '@/types';
import { apiService } from '@/utils/api';

const RentmanAuthentication: React.FC = () => {
  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(null);
  const [formData, setFormData] = useState<Partial<RentmanAuthType>>({
    token: ''
  });
  const [showModal, setShowModal] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);

  const queryClient = useQueryClient();

  // Get sites
  const { data: sites = [] } = useQuery<Site[]>('sites', apiService.getSites);

  // Get Rentman auth for selected site
  const { data: rentmanAuthData, isLoading } = useQuery<RentmanAuthType[]>(
    ['rentmanAuth', selectedSiteId],
    () => apiService.getRentmanAuthentication(selectedSiteId!),
    {
      enabled: !!selectedSiteId,
      onSuccess: (data) => {
        const auth = data?.[0];
        if (auth) {
          setFormData({
            token: auth.token || ''
          });
        }
      }
    }
  );

  const rentmanAuth = rentmanAuthData?.[0];

  // Set first site as default
  useEffect(() => {
    if (sites.length > 0 && !selectedSiteId) {
      setSelectedSiteId(sites[0].id);
    }
  }, [sites, selectedSiteId]);

  // Save mutation
  const saveMutation = useMutation(
    (data: RentmanAuthType) => {
      if (rentmanAuth?.id) {
        return apiService.updateRentmanAuthentication(selectedSiteId!, data);
      } else {
        return apiService.createRentmanAuthentication(selectedSiteId!, data);
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['rentmanAuth', selectedSiteId]);
        setMessage({ type: 'success', text: 'Rentman Authentication Settings saved' });
        setShowModal(false);
        setTimeout(() => setMessage(null), 3000);
      },
      onError: () => {
        setMessage({ type: 'error', text: 'Something went wrong' });
        setTimeout(() => setMessage(null), 3000);
      }
    }
  );

  // Delete mutation
  const deleteMutation = useMutation(
    () => apiService.deleteRentmanAuthentication(selectedSiteId!),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['rentmanAuth', selectedSiteId]);
        setFormData({ token: '' });
        setMessage({ type: 'success', text: 'Rentman Authentication Settings deleted' });
        setTimeout(() => setMessage(null), 3000);
      },
      onError: () => {
        setMessage({ type: 'error', text: 'Something went wrong' });
        setTimeout(() => setMessage(null), 3000);
      }
    }
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const submitData: RentmanAuthType = {
      ...formData,
      site_id: selectedSiteId!
    } as RentmanAuthType;
    
    saveMutation.mutate(submitData);
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this integration?')) {
      deleteMutation.mutate();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="container mx-auto p-6">
      <h1 className="text-2xl font-bold mb-6">Rentman Authentication Settings</h1>

      {/* Messages */}
      {message && (
        <div className={`mb-4 p-4 rounded ${
          message.type === 'success' ? 'bg-green-100 text-green-800' :
          message.type === 'error' ? 'bg-red-100 text-red-800' :
          'bg-blue-100 text-blue-800'
        }`}>
          {message.text}
        </div>
      )}

      {isLoading && (
        <div className="mb-4 p-4 bg-blue-100 text-blue-800 rounded">
          Loading...
        </div>
      )}

      {/* Main Form */}
      <div className="bg-white rounded-lg shadow p-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="site_id" className="block text-sm font-medium text-gray-700 mb-2">
              Site ID
            </label>
            <select
              id="site_id"
              name="site_id"
              value={selectedSiteId || ''}
              onChange={(e) => setSelectedSiteId(Number(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">Select a site</option>
              {sites.map(site => (
                <option key={site.id} value={site.id}>
                  {site.url}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="token" className="block text-sm font-medium text-gray-700 mb-2">
              Token
            </label>
            <input
              type="text"
              id="token"
              name="token"
              value={formData.token || ''}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div className="flex justify-end pt-6 space-x-3">
            <button
              type="submit"
              className="px-4 py-2 text-white rounded-md hover:opacity-90 focus:outline-none focus:ring-2"
              style={{ backgroundColor: 'hsl(15 100% 52%)' }}
              disabled={saveMutation.isLoading}
            >
              {saveMutation.isLoading ? 'Saving...' : 'Save'}
            </button>

            {rentmanAuth?.id && (
              <button
                type="button"
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                disabled={deleteMutation.isLoading}
              >
                {deleteMutation.isLoading ? 'Deleting...' : 'Delete'}
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Add Auth Button (when no existing auth) */}
      {!rentmanAuth && (
        <div className="mt-6">
          <button
            onClick={() => setShowModal(true)}
            className="px-4 py-2 text-white rounded-md hover:opacity-90 focus:outline-none focus:ring-2"
            style={{ backgroundColor: 'hsl(15 100% 52%)' }}
          >
            Save Auth
          </button>
        </div>
      )}

      {/* Modal for adding new auth */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Add RentmanAuthentication</h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="modal_site_id" className="block text-sm font-medium text-gray-700 mb-2">
                  Site ID
                </label>
                <select
                  id="modal_site_id"
                  name="site_id"
                  value={selectedSiteId || ''}
                  onChange={(e) => setSelectedSiteId(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select a site</option>
                  {sites.map(site => (
                    <option key={site.id} value={site.id}>
                      {site.url}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="modal_token" className="block text-sm font-medium text-gray-700 mb-2">
                  Token
                </label>
                <input
                  type="text"
                  id="modal_token"
                  name="token"
                  value={formData.token || ''}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div className="flex justify-end pt-6 space-x-3">
                <button
                  type="submit"
                  className="px-4 py-2 text-white rounded-md hover:opacity-90 focus:outline-none focus:ring-2"
              style={{ backgroundColor: 'hsl(15 100% 52%)' }}
                  disabled={saveMutation.isLoading}
                >
                  {saveMutation.isLoading ? 'Adding...' : 'Add'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default RentmanAuthentication;
